import { getTagById, getTagCustomers, updateTag } from '@app/services/tag';
import { createTagValidation } from '@app/utils/validations';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export const useTagViewPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [renameOpen, setRenameOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState({ page: 1, limit: 10 });
  const renameForm = useForm({ resolver: createTagValidation });

  const { data: tag, refetch: refetchTag } = useQuery({
    queryKey: ['tag', id],
    queryFn: () => getTagById(id as string).then((res) => res.data.tag),
    enabled: !!id,
  });

  const {
    data: tagCustomers,
    isLoading: isLoadingCustomers,
    refetch: refetchCustomers,
  } = useQuery({
    queryKey: ['tag-customers', id, customerQuery.page, customerQuery.limit],
    queryFn: () => getTagCustomers(id as string, customerQuery).then((res) => res.data),
    enabled: !!id,
  });

  useEffect(() => {
    if (tag) renameForm.reset({ name: tag.name });
  }, [tag]);

  const { mutateAsync: rename, isPending } = useMutation({
    mutationKey: ['update-tag', id],
    mutationFn: (name: string) => updateTag(id as string, { name }),
    onSuccess: () => {
      refetchTag();
      setRenameOpen(false);
      toast.success('Tag updated successfully!');
    },
    onError: () => {
      toast.error('Error updating tag');
    },
  });

  const handleRename = async (values: FieldValues) => {
    await rename(values.name);
  };

  return {
    navigate,
    tag,
    tagCustomers,
    isLoadingCustomers,
    refetchCustomers,
    customerQuery,
    handleNextPage: () => setCustomerQuery((prev) => ({ ...prev, page: prev.page + 1 })),
    handlePrevPage: () => setCustomerQuery((prev) => ({ ...prev, page: Math.max(prev.page - 1, 1) })),
    handleLimit: (limit = 10) => setCustomerQuery({ page: 1, limit }),
    renameOpen,
    setRenameOpen,
    renameForm,
    handleRename,
    isPending,
  };
};
